/**
 * The banner's box as the service renders it (`uniquify._banner_filters`), in CSS terms — so the
 * editor's live preview lays the banner clip over the video exactly where the final file will have it.
 *
 * The clip is *fitted* into a box `width × height` of the frame with its proportions kept (never
 * stretched, never cropped), centred horizontally, anchored to the top or bottom edge with `margin` of
 * the frame between the edge and the box. It is laid over the finished frame — strip included — so it
 * can go anywhere; the only rule is that the box stays inside the frame.
 */

export const BANNER_WIDTH: [number, number] = [0.05, 1.0]
export const BANNER_HEIGHT: [number, number] = [0.02, 1.0]
export const BANNER_MARGIN: [number, number] = [0.0, 0.98]

export interface BannerGeometry {
  width: number
  height: number
  anchor: 'top' | 'bottom'
  margin: number
}

/** The box as percentages of the frame for an absolutely positioned element inside a 9:16 frame. */
export function bannerBoxStyle(g: BannerGeometry): { left: string; width: string; height: string; top?: string; bottom?: string } {
  const edge = `${g.margin * 100}%`
  return {
    left: `${((1 - g.width) / 2) * 100}%`,
    width: `${g.width * 100}%`,
    height: `${g.height * 100}%`,
    ...(g.anchor === 'top' ? { top: edge } : { bottom: edge }),
  }
}

const clamp = (v: number, [lo, hi]: [number, number]) => Math.min(hi, Math.max(lo, v))

/** The geometry kept inside what the service accepts; `margin` yields to `height` when they overflow the frame. */
export function clampBanner(g: BannerGeometry): BannerGeometry {
  const width = clamp(g.width, BANNER_WIDTH)
  const height = clamp(g.height, BANNER_HEIGHT)
  const margin = clamp(Math.min(g.margin, 1 - height), BANNER_MARGIN)
  return { width, height, anchor: g.anchor, margin }
}
